import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { bioApi, type BioSection } from '../api/bioApi'
import { useToast } from '@/hooks/use-toast'

export const bioKeys = { all: ['easel', 'bio'] as const }

export function useBio() {
  return useQuery({ queryKey: bioKeys.all, queryFn: bioApi.get })
}

export function useRegenerateBio() {
  const qc = useQueryClient()
  const { toast } = useToast()
  return useMutation({
    mutationFn: bioApi.regenerate,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: bioKeys.all })
      toast({
        title: 'Draft written',
        description: 'Read it through and edit anything that is not true before publishing.',
      })
    },
    onError: (e: any) =>
      toast({
        title: 'Could not write a draft',
        description:
          e?.response?.data?.error?.message ??
          e?.response?.data?.message ??
          'Something went wrong writing the draft.',
        variant: 'destructive',
      }),
  })
}

export function usePublishBio() {
  const qc = useQueryClient()
  const { toast } = useToast()
  return useMutation({
    mutationFn: ({
      sections,
      targets,
    }: {
      sections: BioSection[]
      targets?: { publishToMarketplace?: boolean; publishToStorefront?: boolean }
    }) => bioApi.publish(sections, targets),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: bioKeys.all })
      // Say where it actually went. A publish that saved the chapters but could not reach the
      // marketplace is a different outcome from one that worked, and the artist is the person who
      // will otherwise go looking at an unchanged page.
      const d = result.distribution
      const landed: string[] = []
      const missed: string[] = []
      if (d?.shopify?.attempted) (d.shopify.ok ? landed : missed).push('marketplace page')
      if (d?.storefront?.attempted) (d.storefront.ok ? landed : missed).push('your site')
      landed.push('your profile')

      toast({
        title: missed.length ? 'Published, with one place to retry' : 'Published',
        description: missed.length
          ? `Live on ${landed.join(' and ')}. Could not reach ${missed.join(' and ')} — publish again to retry.`
          : `Live on ${landed.join(', ')}.`,
        variant: missed.length ? 'destructive' : undefined,
      })
    },
    onError: (e: any) =>
      toast({
        title: 'Not published',
        description: e?.response?.data?.message ?? 'Could not publish your biography.',
        variant: 'destructive',
      }),
  })
}
